/**
 * @author Ahmed Samer
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2019-01-21
 * @description Implementation of document handler utilities to be used in the app.
 * @filename document-helper.ts
 */
import { MediaAssetFilePageFormInput, MediaAssetPageFormInput } from "../component-configuration/cms-pages";
import { MediaAsset } from "./mutation-operation/media-asset";
import { MediaAssetFile } from "./mutation-operation/media-asset-file";
import { S3Helper } from "./s3";
/**
 *
 * Utility class responsible for OptimizedImage calls
 * @export
 * @class DocumentHelper
 */
export class DocumentHelper {
  /**
   * Function to upload image binary content to s3 bucket
   *
   * @static
   * @param {{ fileBinaryContent: Blob;
   *     fileSize: number;
   *     fileName: string;
   *     mimeType: string;}} staticFileInformation
   * @returns Returns a promise containing a upload results
   * @memberof DocumentHelper
   */
  static uploadDocumentToS3(
    staticFileInformation: {
      fileBinaryContent: Blob;
      fileSize: number;
      fileName: string;
      mimeType: string;
    },
    progressCall?: Function
  ): Promise<SendData> {
    let promise: Promise<SendData>;
    // attempt to validate image
    // size      : we validate size to prevent uploading corrupted files
    // mime-type : we validate mime-type to prevent uploading
    //             binary data without magic headers
    if (
      staticFileInformation.fileBinaryContent.size <= 0 ||
      staticFileInformation.mimeType.length < 1
    ) {
      promise = Promise.reject(
        new Error(
          "[a54D4l3OJke14Q3oNbf0Ow] invalid document provided [mime-type/size]"
        )
      );
    } else {
      // create a promise to handle s3 upload providing needed file data
      // file Binary content
      // generated file name
      // file mime-type
      promise = S3Helper.uploadToS3(
        staticFileInformation.fileBinaryContent,
        staticFileInformation.fileName,
        {
          ContentType: staticFileInformation.mimeType
        },
        progressCall
      );
    }
    return promise;
  }

  /**
   *
   * @static
   * @param {MediaAssetFileFunctionInput} inputParameters
   * @returns {Promise<any>}
   * @memberof DocumentHelper
   */
  static createMediaAssetFile(
    inputParameters: MediaAssetFilePageFormInput
  ): Promise<any> {
    return MediaAssetFile.addMediaAssetFile(inputParameters);
  }

  /**
   *
   * @static
   * @param {MediaAssetFileFunctionInput} inputParameters
   * @returns {Promise<any>}
   * @memberof DocumentHelper
   */
  static createMediaAsset(
    inputParameters: MediaAssetPageFormInput
  ): Promise<any> {
    return MediaAsset.addMediaAsset(inputParameters);
  }
}
